import type { AnchoredRow } from '../types/anchoredRowTypes'
import type { CameraLucidaPlateModel } from '../types/cameraLucidaTypes'

const UUID_TOKEN = /[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}/i
const UUID_TOKENS = /[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}/gi

const countLabel = (count: number, singular: string, plural = `${singular}s`): string => (
  `${count} ${count === 1 ? singular : plural}`
)

export const describeCameraLucidaScope = ({
  rowIds,
  witnessIds = [],
}: {
  rowIds: readonly string[]
  witnessIds?: readonly string[]
}): string => [
  rowIds.length ? countLabel(rowIds.length, 'row') : '',
  witnessIds.length ? countLabel(witnessIds.length, 'witness', 'witnesses') : '',
].filter(Boolean).join(' · ')

export interface CameraLucidaIdentityPresentation {
  rowLabel: (rowId: string) => string
  witnessLabel: (witnessId: string) => string
  safeAgentText: (text: string) => string
}

export const createCameraLucidaIdentityPresentation = ({
  model,
  rows,
}: {
  model: CameraLucidaPlateModel
  rows: AnchoredRow[]
}): CameraLucidaIdentityPresentation => {
  const rowLabels = new Map(model.rows.map(row => [row.id, row.label || `row ${row.ordinal}`]))
  const witnessLabels = new Map(model.witnesses.map(witness => [witness.id, witness.siglum || 'unresolved witness']))
  const internalLabels = new Map<string, string>()

  rowLabels.forEach((label, id) => {
    if (UUID_TOKEN.test(id)) internalLabels.set(id, label)
  })
  witnessLabels.forEach((label, id) => {
    if (UUID_TOKEN.test(id)) internalLabels.set(id, label)
  })
  rows.forEach(row => {
    const rowLabel = rowLabels.get(row.id) || 'unresolved row'
    row.unitMappings.forEach(mapping => {
      if (!UUID_TOKEN.test(mapping.unitUuid)) return
      const witnessLabel = witnessLabels.get(mapping.witnessId) || 'unresolved witness'
      const unitLabel = mapping.unitLabel?.trim() || `${witnessLabel} unit on ${rowLabel}`
      internalLabels.set(mapping.unitUuid, unitLabel)
    })
  })

  const replacements = Array.from(internalLabels.entries()).sort(([left], [right]) => right.length - left.length)
  return {
    rowLabel: rowId => rowLabels.get(rowId) || 'unresolved row',
    witnessLabel: witnessId => witnessLabels.get(witnessId) || 'unresolved witness',
    safeAgentText: text => {
      let safeText = text
      replacements.forEach(([internalId, label]) => {
        safeText = safeText.split(internalId).join(label)
      })
      return safeText.replace(UUID_TOKENS, 'internal reference')
    },
  }
}
